/** Turns two saved configs into the before and after partials that design undo restores. */
import { configChangeLabel, MEDIA, mediaId } from './designHistoryLabels'
import type {
  DesignChange,
  DesignSnapshot,
  LabelledConfig,
  LabelledPatch,
} from './designHistoryLabels.types'

type Tree = Record<string, unknown>
type Entry = [key: string, before: unknown, after: unknown]

function isTree(value: unknown): value is Tree {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sameLeaf(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

// Builds matching partials with only the leaves that differ; a missing leaf becomes null.
function diffTrees(before: unknown, after: unknown): [Tree, Tree] | undefined {
  const left = isTree(before) ? before : {}
  const right = isTree(after) ? after : {}
  const entries = [...new Set([...Object.keys(left), ...Object.keys(right)])]
    .map((key): Entry | undefined => {
      const a = left[key]
      const b = right[key]

      if (isTree(a) || isTree(b)) {
        const nested = diffTrees(a, b)

        return nested && [key, nested[0], nested[1]]
      }

      return sameLeaf(a, b) ? undefined : [key, a ?? null, b ?? null]
    })
    .filter((entry) => entry !== undefined)

  if (entries.length === 0) return undefined

  return [
    Object.fromEntries(entries.map(([key, a]) => [key, a])),
    Object.fromEntries(entries.map(([key, , b]) => [key, b])),
  ]
}

/** Returns the design and media partials an edit changed, or undefined when it changed neither. */
export function buildDesignChange(
  before: LabelledConfig | undefined,
  after: LabelledConfig,
  patch?: LabelledPatch
): DesignChange | undefined {
  const design = diffTrees(before?.designConfig, after.designConfig)
  const media = MEDIA.filter(
    (spec) => mediaId(before, spec) !== mediaId(after, spec)
  )

  if (!design && media.length === 0) return undefined

  const side = (config: LabelledConfig | undefined, tree?: Tree) => {
    const snapshot: DesignSnapshot = tree ? { designConfig: tree } : {}

    for (const spec of media) snapshot[spec.field] = mediaId(config, spec)

    return snapshot
  }

  return {
    label: configChangeLabel(before, after, patch),
    before: side(before, design?.[0]),
    after: side(after, design?.[1]),
  }
}
